import { useEffect, useState } from 'react';
import { Modal } from '../../components/Modal';
import { api, extractErrorMessage } from '../../api/client';
import { normalizarTexto } from '../../lib/texto';
import { CodigoArticulo } from '../../components/CodigoArticulo';
import { textoArticulo, useIdentidades } from '../../lib/identidadArticulos';

/** Respuesta de GET remitos/pendientes-de-recibir: sin precios ni ningún otro dato monetario. */
export interface LineaPendiente {
  id: number; sku: string | null; codigoProveedor: string | null; descripcion: string; unidadMedida: string;
  /** recibido: en remitos confirmados; enBorrador: en remitos sin confirmar; pendiente: lo que falta descontando los dos. */
  pedido: number; recibido: number; enBorrador?: number; pendiente: number;
}
export interface OrdenPendiente {
  id: number; numeroOrden: string | null; proveedor: { id: number; nombre: string };
  /** AAAA-MM-DD */
  fecha: string;
  /** AAAA-MM-DD, o null si la OC no tiene fecha de entrega. */
  fechaEntrega: string | null;
  estado: string;
  lineas: LineaPendiente[];
}

const URL_PENDIENTES = '/api/v3/remitos/pendientes-de-recibir';

/** AAAA-MM-DD → DD/MM/AAAA, sin pasar por Date (evita el corrimiento de un día por la zona horaria). */
const fechaCorta = (iso: string) => iso.slice(0, 10).split('-').reverse().join('/');
const cantidad = (n: number) => n.toLocaleString('es-AR', { maximumFractionDigits: 4 });

const estadoColors: Record<string, string> = { pendiente: 'bg-amber-100 text-amber-700', parcial: 'bg-sky-100 text-sky-700' };

/** OC que pasan el filtro de proveedor y la búsqueda: por número de OC, todas sus líneas; si no, las líneas que coinciden. */
function filtrar(ordenes: OrdenPendiente[], proveedorId: string, busqueda: string, codigoDe: (sku: string) => string): OrdenPendiente[] {
  const q = normalizarTexto(busqueda);
  return ordenes.flatMap(oc => {
    if (proveedorId && String(oc.proveedor.id) !== proveedorId) return [];
    if (!q || normalizarTexto(`${oc.numeroOrden ?? ''} ${oc.id}`).includes(q)) return [oc];
    const lineas = oc.lineas.filter(l => normalizarTexto(`${l.sku ?? ''} ${l.sku ? codigoDe(l.sku) : ''} ${l.codigoProveedor ?? ''} ${l.descripcion}`).includes(q));
    return lineas.length > 0 ? [{ ...oc, lineas }] : [];
  });
}

/**
 * Ventana de solo lectura con las OC que faltan recibir (pantalla de Remitos). No muestra precios: el backend
 * tampoco los manda. Una OC por bloque, contraída; al buscar se expanden las que coinciden.
 *
 * Con `onRecibir` (quien la abre decide si el usuario puede crear remitos) cada OC muestra "Recibir", que
 * entrega la OC completa aunque la búsqueda esté mostrando solo algunas de sus líneas. Mientras `recibiendo`
 * (id de la OC que se está cargando) no sea null, todos los "Recibir" quedan deshabilitados.
 */
export function PendientesDeRecibir({ open, onClose, onRecibir, recibiendo = null }: {
  open: boolean; onClose: () => void; onRecibir?: (oc: OrdenPendiente) => void; recibiendo?: number | null;
}) {
  const [ordenes, setOrdenes] = useState<OrdenPendiente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [intento, setIntento] = useState(0);
  const [proveedorId, setProveedorId] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [expandidas, setExpandidas] = useState<Set<number>>(new Set());

  // Cada vez que se abre, arranca sin filtros y con las OC contraídas.
  useEffect(() => {
    if (!open) return;
    setProveedorId(''); setBusqueda(''); setExpandidas(new Set());
  }, [open]);

  // Se vuelve a pedir en cada apertura: lo pendiente cambia con cada remito cargado.
  useEffect(() => {
    if (!open) return;
    let vigente = true;
    setCargando(true); setError(null);
    api.get<OrdenPendiente[]>(URL_PENDIENTES)
      .then(r => { if (vigente) setOrdenes(r ?? []); })
      .catch(e => { if (vigente) { setOrdenes([]); setError(extractErrorMessage(e)); } })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [open, intento]);

  const proveedores = [...new Map(ordenes.map(o => [o.proveedor.id, o.proveedor])).values()]
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  const identidad = useIdentidades(ordenes.flatMap(o => o.lineas.map(l => l.sku)));
  const visibles = filtrar(ordenes, proveedorId, busqueda, sku => textoArticulo(sku, identidad(sku)));
  const todasExpandidas = visibles.length > 0 && visibles.every(o => expandidas.has(o.id));
  const cantidadLineas = visibles.reduce((s, o) => s + o.lineas.length, 0);
  // Sin nada por recibir fuera de los borradores (la OC sale porque esos remitos todavía no se confirmaron).
  const soloBorradores = new Set(ordenes.filter(o => o.lineas.every(l => l.pendiente <= 0)).map(o => o.id));

  const alternar = (id: number) => setExpandidas(prev => {
    const nuevo = new Set(prev);
    if (nuevo.has(id)) nuevo.delete(id); else nuevo.add(id);
    return nuevo;
  });
  const buscar = (texto: string) => {
    setBusqueda(texto);
    if (texto.trim()) setExpandidas(new Set(ordenes.map(o => o.id)));
  };

  return (
    <Modal open={open} onClose={onClose} title="Pendientes de recibir" size="xl">
      <div className="space-y-4">
        {cargando ? (
          <p role="status" className="text-sm text-slate-500 py-6 text-center">Cargando órdenes de compra…</p>
        ) : error ? (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span>No se pudieron cargar las OC pendientes: {error}</span>
            <button onClick={() => setIntento(i => i + 1)} className="self-start sm:self-auto px-3 py-1.5 rounded-lg text-sm font-medium text-red-700 hover:bg-red-100 border border-red-300">Reintentar</button>
          </div>
        ) : ordenes.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">No hay órdenes de compra pendientes de recibir</p>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="sm:w-60">
                <label htmlFor="pendientes-proveedor" className="block text-sm font-medium mb-1">Proveedor</label>
                <select id="pendientes-proveedor" value={proveedorId} onChange={e => setProveedorId(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                  <option value="">Todos los proveedores</option>
                  {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
              </div>
              <div className="flex-1">
                <label htmlFor="pendientes-buscar" className="block text-sm font-medium mb-1">Buscar</label>
                <input id="pendientes-buscar" type="search" value={busqueda} onChange={e => buscar(e.target.value)} placeholder="N° de OC, SKU o descripción" className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <button
                onClick={() => setExpandidas(todasExpandidas ? new Set() : new Set(visibles.map(o => o.id)))}
                disabled={visibles.length === 0}
                className="px-3 py-2 rounded-lg text-sm font-medium text-slate-700 border border-slate-300 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {todasExpandidas ? 'Contraer todas' : 'Expandir todas'}
              </button>
            </div>
            <p className="text-xs text-slate-500">
              {visibles.length} OC · {cantidadLineas} {cantidadLineas === 1 ? 'línea pendiente' : 'líneas pendientes'} · primero la entrega más vieja · lo que ya está en un remito en borrador se descuenta y se muestra aparte
            </p>
            {visibles.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Ninguna OC coincide con la búsqueda</p>
            ) : (
              <ul className="space-y-2">
                {visibles.map(oc => {
                  const abierta = expandidas.has(oc.id);
                  return (
                    <li key={oc.id} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="flex items-stretch bg-slate-50">
                        <button
                          onClick={() => alternar(oc.id)}
                          aria-expanded={abierta}
                          aria-controls={`pendientes-oc-${oc.id}`}
                          className="flex-1 min-w-0 text-left px-3 py-2.5 hover:bg-slate-100 flex items-start gap-2"
                        >
                          <span className="text-slate-400 text-xs mt-1 shrink-0" aria-hidden="true">{abierta ? '▾' : '▸'}</span>
                          <span className="flex-1 min-w-0">
                            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span className="font-semibold text-slate-800">OC #{oc.id}</span>
                              {oc.numeroOrden && <span className="text-sm text-slate-500">N° {oc.numeroOrden}</span>}
                              <span className="text-sm text-slate-700">· {oc.proveedor.nombre}</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${estadoColors[oc.estado] ?? 'bg-slate-100 text-slate-600'}`}>{oc.estado}</span>
                              {soloBorradores.has(oc.id) && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">Todo en remitos en borrador: falta que los confirmen</span>}
                            </span>
                            <span className="flex flex-wrap gap-x-3 text-xs text-slate-500 mt-0.5">
                              <span>Fecha {fechaCorta(oc.fecha)}</span>
                              <span>Entrega {oc.fechaEntrega ? fechaCorta(oc.fechaEntrega) : 'Sin fecha'}</span>
                              <span>{oc.lineas.length} {oc.lineas.length === 1 ? 'línea' : 'líneas'}</span>
                            </span>
                          </span>
                        </button>
                        {onRecibir && (
                          <button
                            onClick={() => onRecibir(ordenes.find(o => o.id === oc.id) ?? oc)}
                            disabled={recibiendo !== null}
                            title="Cargar un remito nuevo con lo pendiente de esta OC (en el remito podés sumar otras OC del mismo proveedor)"
                            className="shrink-0 self-center mx-2 px-3 py-1.5 rounded-lg text-sm font-medium text-brand-700 border border-brand-300 bg-white hover:bg-brand-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {recibiendo === oc.id ? 'Cargando…' : 'Recibir'}
                          </button>
                        )}
                      </div>
                      {abierta && (
                        <div id={`pendientes-oc-${oc.id}`} className="table-wrap overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-white text-xs text-slate-500">
                              <tr>
                                <th className="text-left px-2 py-1.5 font-medium">SKU</th>
                                <th className="text-left px-2 py-1.5 font-medium whitespace-nowrap">Cód. proveedor</th>
                                <th className="text-left px-2 py-1.5 font-medium min-w-[10rem]">Descripción</th>
                                <th className="text-center px-2 py-1.5 font-medium">UM</th>
                                <th className="text-right px-2 py-1.5 font-medium">Pedido</th>
                                <th className="text-right px-2 py-1.5 font-medium">Recibido</th>
                                <th className="text-right px-2 py-1.5 font-medium">Pendiente</th>
                              </tr>
                            </thead>
                            <tbody>
                              {oc.lineas.map(l => (
                                <tr key={l.id} className="border-t border-slate-100">
                                  <td className="px-2 py-1.5 font-medium whitespace-nowrap">{l.sku ? <CodigoArticulo sku={l.sku} /> : '—'}</td>
                                  <td className="px-2 py-1.5 whitespace-nowrap">{l.codigoProveedor || '—'}</td>
                                  <td className="px-2 py-1.5">{l.descripcion}</td>
                                  <td className="text-center px-2 py-1.5 text-slate-500">{l.unidadMedida}</td>
                                  <td className="text-right px-2 py-1.5 text-slate-500">{cantidad(l.pedido)}</td>
                                  <td className="text-right px-2 py-1.5 text-slate-500">{cantidad(l.recibido)}</td>
                                  <td className="text-right px-2 py-1.5 font-semibold text-amber-700">
                                    {cantidad(l.pendiente)}
                                    {!!l.enBorrador && <span className="block text-xs font-normal text-slate-500 whitespace-nowrap">({cantidad(l.enBorrador)} más en un remito en borrador)</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
